import MDBox from "components/MDBox";
import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Icon, MenuItem, FormControl, InputLabel, Select, Tooltip, IconButton } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { getProducts, getManufacturers } from "network/service";
import MDSnackbar from "components/MDSnackbar";

// New items start as PCS (the unit vendor bookings use); staff can change it.
const DEFAULT_UOM = "PCS";
let init_val = { id: null, name: "", hsn: "", uom: DEFAULT_UOM, quantity: "", rate: "", manufacturer: "" };
export const UOM_OPTIONS = ["PKTS", "NOS", "STRIPS", "BOX", "BOT", "PCS"];
const UOM_SELECT_STYLE = { height: "1.4375em", padding: 20 };
const CELL_INPUT = { minWidth: 90 };
// Name boxes are multi-line (they grow for long names) — but a name is one line,
// so Enter / pasted line breaks become spaces.
const oneLine = (s) => String(s || "").replace(/[\r\n]+/g, " ");

const uomMenuItems = () =>
    UOM_OPTIONS.map((option) => (
        <MenuItem key={option} value={option}>
            {option}
        </MenuItem>
    ));

/**
 * Line items of one box.
 *
 * Rows are rendered straight from `props.items` on every render. They used to be
 * built inside a useEffect that only re-ran when this box's items changed, so
 * their handlers captured a stale `updateItems` (and through it a stale list of
 * boxes) — editing box 1 then wrote back an old box list and box 2 vanished.
 *
 * Props: items, updateItems(items), containsMedicine, uomRequired
 */
export default function ItemsTable(props) {
    const uomRequired = props.uomRequired !== false;
    const items = props.items || [];

    const [products, setProducts] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [tempItem, setTempItem] = useState({ ...init_val });
    const [productSearchTimeoutId, setProductSearchTimeoutId] = useState(null);
    const [notiDetails, setShowDetails] = useState({ show: false });
    const closeSB = () => setShowDetails({ ...notiDetails, show: false });

    const fetchProducts = async (searchText = "") => {
        const query = String(searchText || "").trim();
        const searchParam = query !== "" ? `_search=${encodeURIComponent(query)}&_limit=10` : `_limit=10`;
        try {
            const resp = await getProducts(searchParam);
            setProducts(resp.data || []);
        } catch (err) {
            console.log(err);
            setProducts([]);
        }
    };

    const renderSB = (
        <MDSnackbar
            color={notiDetails.color}
            icon={notiDetails.icon}
            title={notiDetails.title}
            content={notiDetails.message}
            dateTime=""
            open={notiDetails.show}
            onClose={closeSB}
            close={closeSB}
            bgWhite
        />
    );

    const fail = (title, message) => {
        setShowDetails({ show: true, color: "error", icon: "warning", title, message });
        return false;
    };

    const boxItemValidation = () => {
        if (tempItem.name.trim() === "") return fail("Item name error", "Please enter Item name");
        if (tempItem.quantity === "" || isNaN(tempItem.quantity) || parseFloat(tempItem.quantity) <= 0) {
            return fail("Item quantity error", "Please enter Item quantity");
        }
        if (tempItem.rate === "" || isNaN(tempItem.rate) || parseFloat(tempItem.rate) <= 0) {
            return fail("Item rate error", "Please enter Item rate");
        }
        if (uomRequired && !UOM_OPTIONS.includes(tempItem.uom)) {
            return fail("Item UOM error", "Please select Item UOM (or switch off \"Include UOM\")");
        }
        return true;
    };

    const addItem = () => {
        if (!boxItemValidation()) return;
        props.updateItems([
            ...items,
            { ...tempItem, name: tempItem.name.trim(), quantity: parseFloat(tempItem.quantity), rate: parseFloat(tempItem.rate) },
        ]);
        setTempItem({ ...init_val });
    };

    /** Immutable single-field update of one line item. */
    const patchItem = (index, patch) => {
        props.updateItems(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    };

    const duplicateItem = (index) => {
        const copy = { ...items[index] };
        props.updateItems([...items.slice(0, index + 1), copy, ...items.slice(index + 1)]);
    };

    const deleteItem = (index) => {
        props.updateItems(items.filter((_, i) => i !== index));
    };

    const numberValue = (v) => (v === "" || v === null || v === undefined ? "" : v.toString());
    const parseNumber = (v) => (v === "" ? "" : parseFloat(v));

    useEffect(() => {
        fetchProducts("");
    }, []);

    useEffect(() => {
        return () => {
            if (productSearchTimeoutId) clearTimeout(productSearchTimeoutId);
        };
    }, [productSearchTimeoutId]);

    useEffect(() => {
        getManufacturers().then((resp) => {
            setManufacturers(resp.data || []);
        }).catch((err) => {
            console.log(err);
            setManufacturers([]);
        });
    }, [setManufacturers]);

    const lineTotal = items.reduce((sum, it) => sum + (parseFloat(it.rate) || 0) * (parseFloat(it.quantity) || 0), 0);

    return (
        <MDBox border={1} borderColor={"lightblue"} borderRadius={10}>
            {/* ── add a new line item ── */}
            <div style={{ display: "flex", alignItems: "center", margin: 10 }}>
                <div style={{ display: "flex", flexWrap: "wrap", minWidth: "500px", justifyContent: "space-around", width: "100%", alignItems: 'center' }}>
                    <Autocomplete
                        style={{ flex: 2, margin: 10, minWidth: 200 }}
                        freeSolo
                        getOptionLabel={(option) => (typeof option === "string" ? option : option?.name || "")}
                        inputValue={tempItem.name}
                        onInputChange={(event, newInput, reason) => {
                            if (reason === "reset" && !newInput) return;
                            clearTimeout(productSearchTimeoutId);
                            setTempItem((t) => ({ ...t, name: oneLine(newInput), _id: undefined, id: null }));
                            const newTimeoutId = setTimeout(() => {
                                fetchProducts(newInput);
                            }, 500);
                            setProductSearchTimeoutId(newTimeoutId);
                        }}
                        onChange={(event, newVal) => {
                            if (newVal && typeof newVal === "object") {
                                setTempItem({
                                    ...newVal,
                                    quantity: "",
                                    rate: (newVal.rate || "").toString(),
                                    uom: UOM_OPTIONS.includes(newVal.uom) ? newVal.uom : DEFAULT_UOM,
                                    manufacturer: newVal.manufacturer ? newVal.manufacturer : ""
                                });
                            }
                        }}
                        options={products}
                        renderInput={(params) => <MDInput {...params} label="Name" multiline maxRows={6} />}
                    />
                    <MDInput label={"HSN"}
                        style={{ flex: 1, margin: 10 }}
                        value={tempItem.hsn || ""}
                        onChange={(event) => setTempItem({ ...tempItem, hsn: event.target.value })} />
                    <MDInput label={"Quantity"}
                        type="number"
                        inputProps={{ step: "any", min: 0 }}
                        style={{ flex: 1, margin: 10 }}
                        value={(tempItem.quantity || "").toString()}
                        onChange={(event) => setTempItem({ ...tempItem, quantity: event.target.value })} />
                    {uomRequired && (
                        <FormControl style={{ flex: 1, margin: 10 }}>
                            <InputLabel id="uom-create-label">UOM</InputLabel>
                            <Select
                                labelId="uom-create-label"
                                value={tempItem.uom || ""}
                                label="UOM"
                                style={UOM_SELECT_STYLE}
                                onChange={(event) => setTempItem({ ...tempItem, uom: event.target.value })}
                            >
                                {uomMenuItems()}
                            </Select>
                        </FormControl>
                    )}
                    {props.containsMedicine && (
                        <Autocomplete
                            freeSolo
                            style={{ flex: 1.5, margin: 10, minWidth: 220 }}
                            options={manufacturers}
                            getOptionLabel={(option) => (typeof option === "string" ? option : option?.name || "")}
                            value={tempItem.manufacturer}
                            onInputChange={(event, newInputValue) => setTempItem({ ...tempItem, manufacturer: newInputValue })}
                            onChange={(event, newValue) => {
                                if (typeof newValue === "string") setTempItem({ ...tempItem, manufacturer: newValue });
                                else if (newValue && newValue.name) setTempItem({ ...tempItem, manufacturer: newValue.name });
                            }}
                            renderInput={(params) => <MDInput {...params} label="Manufacturer" />}
                        />
                    )}
                    <MDInput label={"Rate"}
                        type="number"
                        inputProps={{ step: "any", min: 0 }}
                        style={{ flex: 1, margin: 10 }}
                        value={(tempItem.rate || "").toString()}
                        onChange={(event) => setTempItem({ ...tempItem, rate: event.target.value })} />
                    <MDButton variant="gradient" color="light" style={{ flex: 1, margin: 10 }} onClick={addItem}>
                        Add Item<Icon>add</Icon>
                    </MDButton>
                </div>
            </div>

            {/* ── existing line items (all fields editable) ── */}
            {items.length > 0 && (
                <MDBox px={1} pb={1} sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                                {["#", "Name", "HSN", ...(uomRequired ? ["UOM"] : []), ...(props.containsMedicine ? ["Manufacturer"] : []), "Rate", "Quantity", "Value", ""].map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: "bold", fontSize: 12 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ width: 32, fontSize: 12 }}>{index + 1}</TableCell>
                                    <TableCell sx={{ minWidth: 260 }}>
                                        {/* Grows downwards for long names (up to 200 characters for vendors). */}
                                        <MDInput
                                            fullWidth
                                            multiline
                                            minRows={1}
                                            maxRows={8}
                                            value={item.name || ""}
                                            onChange={(event) => patchItem(index, { name: oneLine(event.target.value) })}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>
                                        <MDInput
                                            fullWidth
                                            value={item.hsn || ""}
                                            onChange={(event) => patchItem(index, { hsn: event.target.value })}
                                        />
                                    </TableCell>
                                    {uomRequired && (
                                        <TableCell sx={{ minWidth: 110 }}>
                                            <FormControl fullWidth>
                                                <Select
                                                    value={(item.uom || "").toString()}
                                                    style={{ height: "1.4375em", padding: 18 }}
                                                    displayEmpty
                                                    onChange={(event) => patchItem(index, { uom: event.target.value })}
                                                >
                                                    <MenuItem value="" disabled>UOM</MenuItem>
                                                    {uomMenuItems()}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    )}
                                    {props.containsMedicine && (
                                        <TableCell sx={{ minWidth: 200 }}>
                                            <Autocomplete
                                                freeSolo
                                                options={manufacturers}
                                                getOptionLabel={(option) => (typeof option === "string" ? option : option?.name || "")}
                                                inputValue={(item.manufacturer || "").toString()}
                                                onInputChange={(event, v) => patchItem(index, { manufacturer: v })}
                                                renderInput={(params) => <MDInput {...params} />}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell sx={CELL_INPUT}>
                                        <MDInput
                                            type="number"
                                            inputProps={{ step: "any", min: 0 }}
                                            value={numberValue(item.rate)}
                                            onChange={(event) => patchItem(index, { rate: parseNumber(event.target.value) })}
                                        />
                                    </TableCell>
                                    <TableCell sx={CELL_INPUT}>
                                        <MDInput
                                            type="number"
                                            inputProps={{ step: "any", min: 0 }}
                                            value={numberValue(item.quantity)}
                                            onChange={(event) => patchItem(index, { quantity: parseNumber(event.target.value) })}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                        {((parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap", width: 80 }}>
                                        <Tooltip title="Duplicate item">
                                            <IconButton size="small" onClick={() => duplicateItem(index)}>
                                                <Icon fontSize="small">content_copy</Icon>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete item">
                                            <IconButton size="small" color="error" onClick={() => deleteItem(index)}>
                                                <Icon fontSize="small">delete</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={4 + (uomRequired ? 1 : 0) + (props.containsMedicine ? 1 : 0)} sx={{ textAlign: "right", fontSize: 12, fontWeight: "bold" }}>
                                    Box value
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, fontWeight: "bold" }}>{lineTotal.toFixed(2)}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </MDBox>
            )}
            {renderSB}
        </MDBox>
    );
}
